import { StateService, Transition, UIRouter } from '@uirouter/angular';
import { Subscription } from 'rxjs';

import { BootstrapContext } from 'rev-shared/bootstrap/BootstrapContext';
import { IUnsubscribe } from 'rev-shared/push/IUnsubscribe';
import { IVbNg2StateDeclaration } from 'rev-shared/ts-utils/IVbNg2StateDeclaration';
import { LoginRedirectService } from 'rev-shared/security/LoginRedirect.Service';
import { MediaFeaturesService } from 'rev-shared/media/MediaFeatures.Service';
import { MomentLocaleLoader } from 'rev-shared/date/MomentLocaleLoader.Provider';
import { SECURITY_STARTUP_INJECTION_TOKEN } from 'rev-shared/security/SecurityStartup.Provider';
import { SecurityContextService } from 'rev-shared/security/SecurityContext.Service';
import { Unauthorized401Component } from 'rev-shared/security/components/Unauthorized401.Component';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { decodeQueryString } from 'rev-shared/util/Util.Service';
import { getCommonSecurityStatesNg2 } from 'rev-shared/security/components/SecurityComponentsState.Config';
import {
	resolveBootstrapWebcastId,
	resolveSidebarNotifications,
	resolveUserSubscription,
	resolveWebcast,
	resolveWebcastConnection,
	resolveWebcastPushUnsubscribe,
	resolvePollsSubscription,
	mediaFeaturesResolves
} from 'rev-shared/webcast/CommonStateCfg';
import { AnalyticsViewContext } from 'rev-shared/analytics/Constant';

import { EventRegistrationComponent } from 'rev-portal/scheduledEvents/eventRegistration/EventRegistration.Component';
import { WebcastModel } from 'rev-portal/scheduledEvents/webcast/model/WebcastModel';
import { WebcastModelService } from 'rev-portal/scheduledEvents/webcast/model/WebcastModel.Service';
import { INotifications } from 'rev-portal/scheduledEvents/webcast/Webcast.Contract';
import { EVENT_REGISTRATION_AUTHORIZATION_KEYS, EVENT_REGISTRATION_STATE } from 'rev-portal/scheduledEvents/eventRegistration/Constant';
import { ThemeService } from 'rev-portal/branding/Theme.Service';
import { WebcastService } from 'rev-portal/scheduledEvents/webcast/Webcast.Service';

import { EmbedLoginComponent } from '../login/EmbedLogin.Component';
import { EmbedWebcastComponent } from './EmbedWebcast.Component';
import { EmbedWebcastLoginComponent } from './EmbedWebcastLogin.Component';
import { getEmbedVideoStates, getPublicVideoPasswordState } from '../EmbedStates';

const useTokenAuth = BootstrapContext.tokenParam != null;
const queryParams = decodeQueryString();

export function configureUiRouter(uiRouter: UIRouter): void {
	uiRouter.trace.enable('TRANSITION');
}

const initializationResolve = [{
	token: 'EmbedWebcastStartup',
	deps: [MomentLocaleLoader, SECURITY_STARTUP_INJECTION_TOKEN, ThemeService],
	resolveFn: (
		MomentLocaleLoader: MomentLocaleLoader,
		SecurityStartup: Promise<any>,
		ThemeService: ThemeService
	) => {
		return Promise.all([
			MomentLocaleLoader.load(),
			SecurityStartup,
			ThemeService.initialize()
		]);
	}
}, {
	token: 'thumbnailUri',
	resolveFn: () => BootstrapContext.thumbnailUri
}, {
	token: 'analyticsViewContext',
	resolveFn: () => {
		return queryParams?.viewContext || AnalyticsViewContext.Embedded;
	}
},
resolveBootstrapWebcastId
];

export const embedWebcastStates: IVbNg2StateDeclaration[] = [
	...getCommonSecurityStatesNg2(initializationResolve, (useTokenAuth ? EmbedWebcastLoginComponent : EmbedLoginComponent) as any),
	{
		name: 'embed',
		url: '',
		allowAllAccess: true,
		component: EmbedWebcastComponent,
		resolve: [
			...initializationResolve,
			resolveWebcast(webcast => {
				webcast.currentUser.isEventAdmin = false;
				webcast.currentUser.isEventModerator = false;
				webcast.currentUser.canEdit = false;
			}, 'EmbedWebcastStartup'),
			resolveWebcastConnection,
			resolveUserSubscription,
			resolveSidebarNotifications,
			resolveWebcastPushUnsubscribe,
			resolvePollsSubscription,
			...mediaFeaturesResolves,
			{
				token: 'isPreProd',
				resolveFn: () => false
			},
			{
				token: 'accountId',
				resolveFn: () => BootstrapContext.account.id
			},
			{
				token: 'onlyWebcastEngagementUsage',
				resolveFn: () => false
			},
			{
				token: 'params',
				resolveFn: decodeQueryString
			},
			{
				token: 'webcastLinkedVideo',
				deps: [
					'webcast',
					WebcastModelService
				],
				resolveFn: (
					webcast: WebcastModel,
					WebcastModelService: WebcastModelService
				) => {
					if(webcast) {
						return WebcastModelService.updateLinkedVideo(webcast)
							.catch(e => {
								console.error('Error loading recording vod: ', e);
							});
					}
				}
			},
			{
				token: 'assignWebcastEmbeddedContent',
				deps: ['webcast', WebcastService],
				resolveFn: (webcast: WebcastModel, webcastService: WebcastService) => {
					webcastService.getWebcastEmbeddedContent(webcast.id).then(e => {
						webcast.embeddedContent = e?.embeddedContent;
					});
				}
			}
		],
		redirectTo(transition: Transition) {
			const inj = transition.injector();

			return Promise.all([
				inj.getAsync('EmbedWebcastStartup'),
				inj.getAsync('webcast'),
				inj.getAsync('webcastLinkedVideo')
			]).then(values => {
				const webcast: WebcastModel = values[1];
				const UserContext = inj.get<UserContextService>(UserContextService);

				if(
					webcast.webcastStatus.isCompleted &&
					webcast.linkedVideo &&
					webcast.linkedVideo.isActive &&
					webcast.redirectVod
				) {
					return {
						state: 'embedVideo',
						params: { videoId: webcast.linkedVideo.id }
					};
				}

				return UserContext.isUserLoggedIn() ? null : { state: EVENT_REGISTRATION_STATE };
			});
		},
		onExit(transition: Transition) {
			const injector = transition.injector(null, 'exiting');
			Promise.all([
				injector.getAsync<WebcastModel>('webcast'),
				injector.getAsync<Subscription>('webcastConnection'),
				injector.getAsync<INotifications>('sidebarNotifications'),
				injector.getAsync<Subscription>('pollsPushSubscription'),
				injector.getAsync<IUnsubscribe>('userSubscription'),
			])
				.then(([webcast, webcastConnection, sidebarNotifications, pollsPushSubscription, userSubscription]) => {
					sidebarNotifications?.subscription?.unsubscribe();

					webcastConnection?.unsubscribe();

					webcast.attendeeQuestionsSubscription?.unsubscribe();
					pollsPushSubscription?.unsubscribe();
					userSubscription?.();
					webcast.assignCurrentRun(undefined);
				});
		}
	}, {
		name: EVENT_REGISTRATION_STATE,
		url: '/event-registration',
		component: EventRegistrationComponent,
		allowAllAccess: true,
		resolve: [
			...initializationResolve,
			{
				token: 'isOnPrem',
				deps: [UserContextService, MediaFeaturesService],
				resolveFn: (UserContext: UserContextService, MediaFeatures: MediaFeaturesService) => MediaFeatures
					.getFeatures(UserContext.getAccount().id)
					.then(features => features.onPrem)
			}, {
				token: 'isRequireRegistrationEmailVerification',
				deps: [UserContextService, MediaFeaturesService],
				resolveFn: (UserContext: UserContextService, MediaFeatures: MediaFeaturesService) => MediaFeatures
					.getFeatures(UserContext.getAccount().id)
					.then(features => features.requireRegistrationEmailVerification)
			},
			{
				token: 'afterSubmitFn',
				deps: [StateService],
				resolveFn: $state => () => $state.go('embed')
			},
			{
				token: 'webcastUrl',
				resolveFn: () => ''
			}, {
				token: 'webcastInfo',
				deps: [
					LoginRedirectService,
					SecurityContextService,
					WebcastService,
					'webcastId',
					'webcastUrl',
					'EmbedWebcastStartup'
				],
				resolveFn: (
					LoginRedirectService: LoginRedirectService,
					SecurityContext: SecurityContextService,
					WebcastService: WebcastService,
					webcastId: string,
					webcastUrl: string
				) => WebcastService.getWebcastInfo(webcastId)
					.catch(err => {
						if(err.status === 401){
							return Promise.reject({ status:401, data: { reason:'AttendeeRemoved', noLoginRedirect: true } });
						}
						return Promise.reject({ status: 404 });
					})
					.then(webcastInfo => {
						if (!webcastInfo.isPublic
								|| !SecurityContext.checkAuthorizations(EVENT_REGISTRATION_AUTHORIZATION_KEYS)) {
							console.error('login redirect, is public: ', webcastInfo.isPublic);
							window.setTimeout(() => {
								LoginRedirectService.redirectToLogin(webcastUrl);
							}, 100);
							return Promise.reject();
						}
						return webcastInfo;
					}),
			}
		],
	}, {
		name: '401',
		url: '/401',
		component: Unauthorized401Component,
		resolve: [
			{
				token: 'reason',
				deps: [Transition],
				resolveFn: (transition: Transition) => transition.params().reason
			}
		],
		allowAllAccess: true,
		params: {
			reason: {}
		}
	},
	...getEmbedVideoStates('embedVideo', false),
	getPublicVideoPasswordState()
];
